/**
 * The agenda's coarse window: next 24 h, today, or the ledger's own retention span. Each option
 * is computed from `now` directly rather than from the previously fetched window, so switching
 * options always lands on a wall-clock-relative range.
 */

import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import type { Window } from './api/queries';
import { startOfDay } from './dayHeading';
import { cm } from './messages';

export const WINDOW_OPTIONS = ['next24h', 'today', 'sinceEarliest'] as const;
export type WindowOption = (typeof WINDOW_OPTIONS)[number];

/** Days the reaper keeps a slot in `ledger_history` once it leaves the hot ledger. */
const LEDGER_RETENTION_DAYS = 7;
const DAY_MS = 24 * 3600_000;

export function windowFor(option: WindowOption, now: Date): Window {
  switch (option) {
    case 'next24h':
      return { from: now.toISOString(), to: new Date(now.getTime() + DAY_MS).toISOString() };
    case 'today': {
      const start = startOfDay(now);
      return { from: start.toISOString(), to: new Date(start.getTime() + DAY_MS).toISOString() };
    }
    case 'sinceEarliest':
      return {
        from: new Date(now.getTime() - LEDGER_RETENTION_DAYS * DAY_MS).toISOString(),
        to: now.toISOString(),
      };
  }
}

export function WindowSelect(props: {
  value: WindowOption;
  onChange: (option: WindowOption) => void;
}): ReactNode {
  return (
    <Select value={props.value} onValueChange={(next) => props.onChange(next as WindowOption)}>
      <SelectTrigger aria-label={cm('agenda.windowSelect.label')} size="sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {WINDOW_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {cm(`agenda.windowSelect.${option}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
